import React from 'react';
import { format, parseISO } from 'date-fns';
import { useFormik } from 'formik';
import * as yup from 'yup';
import isEmail from 'validator/lib/isEmail';
import {
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  CircularProgress,
  Typography,
  Grid,
  Stack,
  TextField,
  MenuItem,
  InputAdornment,
  Tooltip,
  Box,
  Chip,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import { USA_STATES } from '../../utils/conts';
import { LightTooltip } from '../custom-components/custom-tooltip';

const validationSchemaClient = yup.object({
  email: yup
    .string('Enter the email')
    .email('Please enter a valid email address')
    .required('Email is required')
    .test(
      'is-valid',
      () => `Please enter a valid email address`,
      (value) => (value ? isEmail(value) : new yup.ValidationError('Please enter a valid email address'))
    )
    .max(100, `Email can't be more than 100 characters`),
  secondary_emails: yup.array().of(yup.string().email('Invalid email address')),
  state: yup.string('Enter the state').required('State is required').max(200, `State can't be more than 50 characters`),
  city: yup.string('Enter the city').required('City is required').max(50, `City can't be more than 50 characters`),
  zipcode: yup
    .number('Enter the zip code')
    .required('Zip code is required')
    .test('maxDigits', 'Zip code should have 5 digits', (value) => {
      if (value) {
        return value.toString().length === 5;
      }
      return true;
    }),
  address: yup
    .string('Enter the address')
    .required('Address is required')
    .max(200, `Address can't be more than 200 characters`),
  phone: yup
    .string('Enter the phone number')
    .required('Phone number is required')
    .test('maxDigits', 'Phone number should have 10 digits', (value) => {
      if (value) {
        const cleanedValue = value.replace(/\D/g, '');
        return cleanedValue.length === 10;
      }
      return true;
    }),
});

export default function ClientModals({
  // Modal states
  clientLockAccountModalOpen,
  clientEditAccountModalOpen,
  clientResetPasswordModalOpen,
  clientAccessCredentialsModalOpen,
  clientContractsModalOpen,

  // Loading states
  confirmLoader,
  loading,
  credentialsLoader,
  agreementsLoader,

  // Data
  selectedClient,
  selectedClientAccountStatus,
  clientAccessCredentials,
  clientContracts,

  // Handlers
  onClientLockAccountModalClose,
  onClientEditAccountModalClose,
  onClientResetPasswordModalClose,
  onClientCredentialsModalClose,
  onClientContractsModalClose,
  onClientLockAccount,
  onContractDownload,
  onEditClientSubmit,
  onResetPasswordSubmit,
}) {
  const formikResetPassword = useFormik({
    initialValues: {
      client_password: '',
      client_password2: '',
    },
    onSubmit: async (values) => {
      onResetPasswordSubmit(values);
    },
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      secondary_emails: [],
      phone: '',
      zipcode: '',
      city: '',
      state: '',
      address: '',
    },
    validationSchema: validationSchemaClient,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values) => {
      onEditClientSubmit(values);
    },
  });

  const handleFieldBlur = (fieldName) => {
    formik.setFieldTouched(fieldName, true, false);
  };

  const handleAddSecondaryEmail = () => {
    const email = formik.values.secondary_emails || [];
    console.log(email, 'Add secondary email');
  };

  const handleSecondaryEmailKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      handleAddSecondaryEmail();
    }
  };

  const handleRemoveSecondaryEmail = (index) => {
    const updated = [...formik.values.secondary_emails];
    updated.splice(index, 1);
    formik.setFieldValue('secondary_emails', updated);
  };

  React.useEffect(() => {
    if (selectedClient && clientEditAccountModalOpen) {
      const clientData = {
        ...selectedClient,
        secondary_emails: selectedClient.secondary_emails || [],
      };
      formik.setValues(clientData, false);
    }
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient, clientEditAccountModalOpen]);

  return (
    <>
      {/* Lock Account Modal */}
      <Dialog open={clientLockAccountModalOpen} onClose={onClientLockAccountModalClose}>
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <p>
            Are you sure you want to {selectedClientAccountStatus === 'Active' ? 'lock' : 'unlock'} the client account?
          </p>
        </DialogContent>
        <DialogActions sx={{ padding: 2 }}>
          <Button onClick={onClientLockAccountModalClose} color="primary">
            Cancel
          </Button>
          <LoadingButton
            onClick={onClientLockAccount}
            color="primary"
            variant="contained"
            loading={confirmLoader}
            loadingPosition="start"
            startIcon={confirmLoader ? <CircularProgress size={20} /> : null}
          >
            Confirm
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Edit Account Modal */}
      <Dialog open={clientEditAccountModalOpen} onClose={onClientEditAccountModalClose} fullWidth>
        <DialogTitle>Edit Client Account</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={12} lg={12}>
              <form onSubmit={formik.handleSubmit}>
                <Stack spacing={1} paddingTop={1}>
                  <TextField
                    id="email"
                    name="email"
                    label="Email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={() => handleFieldBlur('email')}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                  />
                  <TextField
                    label="Secondary Emails (Optional)"
                    variant="outlined"
                    fullWidth
                    value=""
                    onChange={() => {}}
                    onKeyDown={handleSecondaryEmailKeyDown}
                    onBlur={() => formik.setFieldTouched('secondary_emails', true, false)}
                    error={formik.touched.secondary_emails && Boolean(formik.errors.secondary_emails)}
                    helperText={formik.touched.secondary_emails && formik.errors.secondary_emails}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="Press Enter, comma, or space to add emails">
                            <KeyboardReturnIcon sx={{ color: 'action.active' }} />
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {(formik.values.secondary_emails || []).map((email, index) => (
                      <Chip
                        key={email}
                        label={email}
                        onDelete={() => handleRemoveSecondaryEmail(index)}
                        color="primary"
                      />
                    ))}
                  </Box>
                  <TextField
                    id="phone"
                    name="phone"
                    label="Phone"
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    onBlur={() => handleFieldBlur('phone')}
                    error={formik.touched.phone && Boolean(formik.errors.phone)}
                    helperText={formik.touched.phone && formik.errors.phone}
                  />
                  <TextField
                    id="zipcode"
                    name="zipcode"
                    label="Zip Code"
                    value={formik.values.zipcode}
                    onChange={formik.handleChange}
                    onBlur={() => handleFieldBlur('zipcode')}
                    error={formik.touched.zipcode && Boolean(formik.errors.zipcode)}
                    helperText={formik.touched.zipcode && formik.errors.zipcode}
                  />
                  <TextField
                    id="state"
                    name="state"
                    label="State"
                    select
                    value={formik.values.state}
                    onChange={(e) => {
                      formik.handleChange(e);
                      formik.setFieldTouched('state', true, false);
                    }}
                    error={formik.touched.state && Boolean(formik.errors.state)}
                    helperText={formik.touched.state && formik.errors.state}
                    SelectProps={{ MenuProps: { disablePortal: true } }}
                  >
                    {USA_STATES.map((state) => (
                      <MenuItem key={state.abbreviation} value={state.abbreviation}>
                        {state.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    id="city"
                    name="city"
                    label="City"
                    value={formik.values.city}
                    onChange={formik.handleChange}
                    onBlur={() => handleFieldBlur('city')}
                    error={formik.touched.city && Boolean(formik.errors.city)}
                    helperText={formik.touched.city && formik.errors.city}
                  />
                  <TextField
                    id="address"
                    name="address"
                    label="Address"
                    value={formik.values.address}
                    onChange={formik.handleChange}
                    onBlur={() => handleFieldBlur('address')}
                    error={formik.touched.address && Boolean(formik.errors.address)}
                    helperText={formik.touched.address && formik.errors.address}
                  />
                  <Stack direction="row" alignItems="center" justifyContent="flex-end" mb={5} spacing="5px">
                    <Button onClick={onClientEditAccountModalClose} color="primary">
                      Cancel
                    </Button>
                    <Typography variant="h4" gutterBottom />
                    <LoadingButton
                      type="submit"
                      variant="contained"
                      loading={loading}
                      loadingPosition="start"
                      startIcon={loading ? <CircularProgress size={20} /> : null}
                    >
                      Edit Client
                    </LoadingButton>
                  </Stack>
                </Stack>
              </form>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={clientResetPasswordModalOpen} onClose={onClientResetPasswordModalClose} fullWidth>
        <DialogTitle>Reset Client Password</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={12} lg={12}>
              <form onSubmit={formikResetPassword.handleSubmit}>
                <Stack spacing={1} paddingTop={1}>
                  <Typography variant="body2">Do you want to send a one-time password to the client?</Typography>
                  <Stack direction="row" alignItems="center" justifyContent="flex-end" mb={5} spacing="5px">
                    <Button onClick={onClientResetPasswordModalClose} color="primary">
                      Cancel
                    </Button>
                    <Typography variant="h4" gutterBottom />
                    <LoadingButton
                      type="submit"
                      variant="contained"
                      loading={loading}
                      loadingPosition="start"
                      startIcon={loading ? <CircularProgress size={20} /> : null}
                    >
                      Reset Password
                    </LoadingButton>
                  </Stack>
                </Stack>
              </form>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Credentials Modal */}
      <Dialog maxWidth={false} open={clientAccessCredentialsModalOpen} onClose={onClientCredentialsModalClose}>
        <DialogTitle>Credentials</DialogTitle>
        <DialogContent>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Platform</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Password</TableCell>
                <TableCell>URL</TableCell>
                <TableCell>Remarks</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {credentialsLoader ? (
                <TableRow>
                  <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : clientAccessCredentials.length === 0 ? (
                <TableRow>
                  <TableCell align="center" colSpan={6}>
                    <Typography variant="body2">No records found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                clientAccessCredentials.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.platform_name}</TableCell>
                    <TableCell>{row.username}</TableCell>
                    <TableCell>{row.password}</TableCell>
                    <TableCell>{row.url}</TableCell>
                    <TableCell>{row.remarks}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClientCredentialsModalClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Contracts Modal */}
      <Dialog maxWidth={false} open={clientContractsModalOpen} onClose={onClientContractsModalClose}>
        <DialogTitle>Agreements</DialogTitle>
        <DialogContent>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Individuals</TableCell>
                <TableCell>Services</TableCell>
                <TableCell>Percentage Terms</TableCell>
                <TableCell>Activation Date</TableCell>
                <TableCell>Created On</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {agreementsLoader ? (
                <TableRow>
                  <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : clientContracts.length === 0 ? (
                <TableRow>
                  <TableCell align="center" colSpan={7}>
                    <Typography variant="body2">No records found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                clientContracts.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.covered_individuals.join(', ')}</TableCell>
                    <TableCell>{row.services.join(', ')}</TableCell>
                    <TableCell>{row.percentage_terms}</TableCell>
                    <TableCell>{format(parseISO(row.activation_date), 'MM-dd-yy')}</TableCell>
                    <TableCell>{format(parseISO(row.created_at), 'MM-dd-yy')}</TableCell>
                    <TableCell>{row.status}</TableCell>
                    <TableCell>
                      <LightTooltip title="Print/Download the agreement.">
                        <Button variant="contained" color="primary" onClick={() => onContractDownload(row.id)}>
                          <DownloadIcon />
                        </Button>
                      </LightTooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClientContractsModalClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
